'use client'
import { useState, useEffect, useRef } from 'react'
import { useSearchParams } from 'next/navigation'
import { getCookies } from 'cookies-next'
import { countryCodes } from '@constants/countryCodes'
import { preferredContact } from '@constants/preferredContact';
import { contactReason } from '@constants/contactReason';

// ─── Resolve multilingual value ───────────────────────────────────────────────

function r(val, lang) {
  if (!val) return ''
  if (typeof val === 'string') return val
  return val[lang] ?? val.en ?? Object.values(val)[0] ?? ''
}

// ─── Icons ────────────────────────────────────────────────────────────────────

const SvgIcons = {
  user:   <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>,
  email:  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>,
  phone:  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.56 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 9.91a16 16 0 0 0 6.29 6.29l1.27-.88a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>,
  line:   <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M21 11.5C21 6.8 16.97 3 12 3S3 6.8 3 11.5c0 4.2 3.37 7.73 8 8.63v-2.94c-3.07-.74-5-2.7-5-5.69 0-3.03 2.69-5.5 6-5.5s6 2.47 6 5.5c0 .65-.13 1.28-.36 1.87L16 14.5v2h-4v-2h2.08A4.5 4.5 0 0 1 7.5 11.5"/></svg>,
  wechat: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9.5 9a.5.5 0 1 0 0-1 .5.5 0 0 0 0 1z" fill="currentColor"/><path d="M14.5 9a.5.5 0 1 0 0-1 .5.5 0 0 0 0 1z" fill="currentColor"/><path d="M8.5 15a.5.5 0 1 0 0-1 .5.5 0 0 0 0 1z" fill="currentColor"/><path d="M12.5 15a.5.5 0 1 0 0-1 .5.5 0 0 0 0 1z" fill="currentColor"/><path d="M2 12c0 4.4 4.03 8 9 8 1.3 0 2.53-.26 3.63-.72L20 21l-1.5-4.05C19.46 15.56 21 13.87 21 12c0-4.42-4.03-8-9-8s-9 3.58-9 8z"/></svg>,
  none: null,
}

const ChevronDown = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="m6 9 6 6 6-6"/>
  </svg>
)

// ─── Country code combobox ────────────────────────────────────────────────────

function CountryCombobox({ value, onChange, inputStyle, radius = 6 }) {
  const [query,   setQuery]   = useState('')
  const [open,    setOpen]    = useState(false)
  const [hovered, setHovered] = useState(null)
  const wrapRef               = useRef(null)

  const filtered = query === ''
    ? countryCodes
    : countryCodes.filter(c =>
        c.country.toLowerCase().includes(query.toLowerCase()) ||
        c.code.toLowerCase().includes(query.toLowerCase())
      )

  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false); setQuery('')
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const select = (code) => { onChange(code); setQuery(''); setOpen(false) }

  return (
    <div ref={wrapRef} style={{ position: 'relative', width: 100, flexShrink: 0 }}>
      <div style={{ position: 'relative' }}>
        {/* FIX: aria-label gives screen readers a meaningful name for this input */}
        <input
          type="text"
          aria-label="Country code"
          value={open ? query : value}
          onChange={e => { setQuery(e.target.value); setOpen(true) }}
          onFocus={() => { setOpen(true); setQuery('') }}
          onClick={e => { e.target.select(); setOpen(true); setQuery('') }}
          style={{ ...inputStyle, width: '100%', paddingInlineEnd: 28 }}
        />
        {/* FIX: aria-label, aria-expanded, aria-haspopup make this button
            announced correctly by screen readers instead of just "button" */}
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          aria-label="Select country code"
          aria-expanded={open}
          aria-haspopup="listbox"
          style={{ position: 'absolute', insetInlineEnd: 6, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', padding: 0, color: '#9ca3af', cursor: 'pointer', display: 'flex' }}
        >
          <ChevronDown />
        </button>
      </div>
      {open && filtered.length > 0 && (
        <div
          role="listbox"
          aria-label="Country codes"
          style={{ position: 'absolute', zIndex: 50, top: '100%', insetInlineStart: 0, width: 280, maxHeight: 200, overflowY: 'auto', background: '#fff', border: '1px solid #e5e7eb', borderRadius: radius, boxShadow: '0 4px 16px rgba(0,0,0,0.12)', marginTop: 2 }}
        >
          {filtered.map(c => (
            <div
              key={c.id}
              role="option"
              aria-selected={value === c.code}
              onMouseDown={() => select(c.code)}
              onMouseEnter={() => setHovered(c.id)}
              onMouseLeave={() => setHovered(null)}
              style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 13, background: hovered === c.id ? '#f3f4f6' : '#fff', color: '#111827' }}
            >
              {c.code} {c.country}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── ContactFormRenderer ──────────────────────────────────────────────────────

export default function ContactFormRenderer({ cardStyle, lang = 'en', userId }) {
  const searchParams = useSearchParams()

  const [isLoading,       setIsLoading]       = useState(false)
  const [hasEnquiry,      setHasEnquiry]      = useState(false)
  const [name,            setName]            = useState('')
  const [email,           setEmail]           = useState('')
  const [message,         setMessage]         = useState('')
  const [phone,           setPhone]           = useState('')
  const [line,            setLine]            = useState('')
  const [wechat,          setWechat]          = useState('')
  const [selectedCountry, setSelectedCountry] = useState(countryCodes[0]?.code ?? '+66')
  const [reason,          setReason]          = useState(contactReason[lang]?.[0])
  const [contact,         setContact]         = useState(preferredContact[lang]?.[0])

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('contactDetails'))
      if (!saved) return
      if (saved.name)            setName(saved.name)
      if (saved.selectedCountry) setSelectedCountry(saved.selectedCountry)
      if (saved.phone)           setPhone(saved.phone)
      if (saved.email)           setEmail(saved.email)
      if (saved.contact)         setContact(saved.contact)
      if (saved.line)            setLine(saved.line)
      if (saved.wechat)          setWechat(saved.wechat)
    } catch {}
  }, [])

  const handleForm = async (e) => {
    e.preventDefault()
    setIsLoading(true)
    const cookies = getCookies()
    localStorage.setItem('contactDetails', JSON.stringify({ name, selectedCountry, phone, email, contact, line, wechat }))
    const { sendGAEvent } = await import('@next/third-parties/google')
    sendGAEvent(
    'event',
    'contactus_enquiry',  
    {
      // event:          'contactus_inquiry',
      // user_email:     email || 'not_provided',
      // user_phonecall: `${selectedCountry}${phone?.replace(/[a-zA-Z\W]/g, '')}`,
      // user_line:      line || 'not_provided',
      reason,
    })
    const fbclid = searchParams.get('fbclid')
    const { sendContactPageForm } = await import('@services/sendContactPageForm')
    await sendContactPageForm({ userId, name, selectedCountry, phone, email, message, contact, line, wechat, reason, likes: cookies.likes ?? undefined, fbclid: fbclid ?? undefined })
    setHasEnquiry(true)
    setIsLoading(false)
  }

  const fs     = cardStyle?.formStyle ?? {}
  const fields = cardStyle?.fields    ?? []

  const inputBase = {
    width: '100%', paddingTop: '8px', paddingBottom: '8px',
    paddingInlineStart: 36, paddingInlineEnd: 10,
    boxSizing: 'border-box', fontFamily: 'inherit',
    fontSize: `${fs.inputFontSize ?? 0.875}rem`, color: fs.inputColor ?? '#111827',
    background: fs.inputBg ?? '#ffffff', outline: 'none',
    border: `${fs.inputBorderWidth ?? 1}px solid ${fs.inputBorderColor ?? '#d1d5db'}`,
    borderRadius: fs.inputRadius ?? 6,
  }
  const inputNoIcon = { ...inputBase, paddingInlineStart: 10, paddingInlineEnd: 10 }
  const inputSelect = { ...inputNoIcon, paddingInlineEnd: 32 }

  const labelStyle = (field) => ({
    display: 'block',
    fontSize:    `${field?.labelSize   ?? fs.labelSize   ?? 0.8125}rem`,
    fontWeight:   field?.labelWeight   ?? fs.labelWeight  ?? '500',
    color:        field?.labelColor    ?? fs.labelColor   ?? '#374151',
    marginBottom: 4,
  })

  const containerStyle = {
    background:   fs.bg           ?? '#ffffff',
    border:       (fs.borderWidth ?? 1) > 0 ? `${fs.borderWidth ?? 1}px solid ${fs.borderColor ?? '#e5e7eb'}` : 'none',
    borderRadius: fs.borderRadius ?? 8,
    boxShadow:    fs.shadow       ?? 'none',
    padding:      fs.padding      ?? 24,
  }

  const getField  = (id) => fields.find(f => f.id === id) ?? {}
  const isEnabled = (id) => { const f = fields.find(f => f.id === id); return f ? f.enabled !== false : true }
  const contactVal = contact?.value ?? contact ?? ''
  const showWhenContact = (...vals) => vals.includes(contactVal)

  const iconWrapper = {
    position: 'absolute', insetInlineStart: 10,
    top: '50%', transform: 'translateY(-50%)',
    color: '#9ca3af', display: 'flex', pointerEvents: 'none',
  }

  const chevronWrapper = {
    position: 'absolute', insetInlineEnd: 10,
    top: '50%', transform: 'translateY(-50%)',
    color: '#9ca3af', pointerEvents: 'none', display: 'flex',
  }

  // FIX: aria-hidden so screen readers skip the decorative asterisk;
  // required inputs are already communicated via the required attribute.
  const requiredAsterisk = <span style={{ color: '#ef4444', marginInlineStart: 2 }} aria-hidden="true">*</span>

  // ── Success state ────────────────────────────────────────────────────────────
  if (hasEnquiry) {
    const align = fs.successTitleAlign ?? 'center'
    return (
      <div style={{ ...containerStyle, display: 'flex', flexDirection: 'column', alignItems: align === 'center' ? 'center' : align === 'end' || align === 'right' ? 'flex-end' : 'flex-start', gap: 12, textAlign: align }}>
        <div style={{ width: 56, height: 56, borderRadius: '50%', background: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>
        </div>
        <div style={{ fontSize: `${fs.successTitleSize ?? 1.25}rem`, fontWeight: fs.successTitleWeight ?? '700', color: fs.successTitleColor ?? '#111827' }}>
          {r(fs.successTitle, lang) || 'Message Sent!'}
        </div>
        <div style={{ fontSize: `${fs.successSubtitleSize ?? 0.9375}rem`, fontWeight: fs.successSubtitleWeight ?? '400', color: fs.successSubtitleColor ?? '#6b7280', lineHeight: 1.6, maxWidth: 340 }}>
          {r(fs.successSubtitle, lang)}
        </div>
      </div>
    )
  }

  // ── Form ─────────────────────────────────────────────────────────────────────
  return (
    <div style={containerStyle}>

      {fs.titleEnabled !== false && (
        <div style={{ fontSize: `${fs.titleSize ?? 1.25}rem`, fontWeight: fs.titleWeight ?? '700', color: fs.titleColor ?? '#111827', textAlign: fs.titleAlign ?? 'center', marginBottom: fs.gap ?? 16 }}>
          {r(fs.titleText, lang) || 'Contact Us'}
        </div>
      )}

      <form onSubmit={handleForm} style={{ display: 'flex', flexDirection: 'column', gap: fs.gap ?? 16 }}>

        {/* Name */}
        {isEnabled('name') && (() => { const fld = getField('name'); return (
          <div>
            {/* FIX: htmlFor links label to input via matching id */}
            <label htmlFor="field-name" style={labelStyle(fld)}>{r(fld.label, lang)}{fld.required !== false && requiredAsterisk}</label>
            <div style={{ position: 'relative' }}>
              <span style={iconWrapper}>{SvgIcons.user}</span>
              <input id="field-name" type="text" style={inputBase} placeholder={r(fld.placeholder, lang)} value={name} onChange={e => setName(e.target.value)} required={fld.required !== false} />
            </div>
          </div>
        )})()}

        {/* Contact Reason */}
        {isEnabled('reason') && (() => { const fld = getField('reason'); return (
          <div>
            {/* FIX: htmlFor + id association for the reason select */}
            <label htmlFor="field-reason" style={labelStyle(fld)}>{r(fld.label, lang)}{fld.required !== false && requiredAsterisk}</label>
            <div style={{ position: 'relative' }}>
              <select
                id="field-reason"
                value={reason?.value ?? reason ?? ''}
                onChange={e => setReason(contactReason[lang]?.find(x => x.value === e.target.value) ?? e.target.value)}
                required={fld.required !== false}
                style={{ ...inputSelect, appearance: 'none', cursor: 'pointer' }}
              >
                {contactReason[lang]?.map((res, i) => <option key={i} value={res.value ?? res}>{res.name ?? res}</option>)}
              </select>
              <span style={chevronWrapper}><ChevronDown /></span>
            </div>
          </div>
        )})()}

        {/* Preferred Contact */}
        {isEnabled('contact') && (() => { const fld = getField('contact'); return (
          <div>
            {/* FIX: htmlFor + id association for the preferred contact select */}
            <label htmlFor="field-contact" style={labelStyle(fld)}>{r(fld.label, lang)}{fld.required !== false && requiredAsterisk}</label>
            <div style={{ position: 'relative' }}>
              <select
                id="field-contact"
                value={contactVal}
                onChange={e => setContact(preferredContact[lang]?.find(x => x.value === e.target.value) ?? e.target.value)}
                required={fld.required !== false}
                style={{ ...inputSelect, appearance: 'none', cursor: 'pointer' }}
              >
                {preferredContact[lang]?.map((cont, i) => <option key={i} value={cont.value ?? cont}>{cont.name ?? cont}</option>)}
              </select>
              <span style={chevronWrapper}><ChevronDown /></span>
            </div>
          </div>
        )})()}

        {/* Phone */}
        {isEnabled('phone') && showWhenContact('phone', 'whatsapp', 'viber') && (() => { const fld = getField('phone'); return (
          <div>
            <label style={labelStyle(fld)}>{r(fld.label, lang)}{fld.required !== false && requiredAsterisk}</label>
            <div style={{ display: 'flex', gap: 6 }}>
              <CountryCombobox value={selectedCountry} onChange={setSelectedCountry} inputStyle={inputNoIcon} radius={fs.inputRadius ?? 6} />
              <div style={{ position: 'relative', flex: 1 }}>
                <span style={iconWrapper}>{SvgIcons.phone}</span>
                {/* FIX: aria-label used here because the label is shared with the
                    country combobox — a visible label can't link to two inputs */}
                <input type="text" aria-label={r(fld.label, lang)} style={inputBase} placeholder={r(fld.placeholder, lang)} value={phone} onChange={e => setPhone(e.target.value)} required={fld.required !== false} />
              </div>
            </div>
          </div>
        )})()}

        {/* LINE */}
        {isEnabled('line') && showWhenContact('line') && (() => { const fld = getField('line'); return (
          <div>
            {/* FIX: htmlFor + id association for LINE input */}
            <label htmlFor="field-line" style={labelStyle(fld)}>{r(fld.label, lang)}{fld.required !== false && requiredAsterisk}</label>
            <div style={{ position: 'relative' }}>
              <span style={iconWrapper}>{SvgIcons.line}</span>
              <input id="field-line" type="text" style={inputBase} placeholder={r(fld.placeholder, lang)} value={line} onChange={e => setLine(e.target.value)} required={fld.required !== false} />
            </div>
          </div>
        )})()}

        {/* WeChat */}
        {isEnabled('wechat') && showWhenContact('wechat') && (() => { const fld = getField('wechat'); return (
          <div>
            {/* FIX: htmlFor + id association for WeChat input */}
            <label htmlFor="field-wechat" style={labelStyle(fld)}>{r(fld.label, lang)}{fld.required !== false && requiredAsterisk}</label>
            <div style={{ position: 'relative' }}>
              <span style={iconWrapper}>{SvgIcons.wechat}</span>
              <input id="field-wechat" type="text" style={inputBase} placeholder={r(fld.placeholder, lang)} value={wechat} onChange={e => setWechat(e.target.value)} required={fld.required !== false} />
            </div>
          </div>
        )})()}

        {/* Email */}
        {isEnabled('email') && (() => { const fld = getField('email'); return (
          <div>
            {/* FIX: htmlFor + id association for email input */}
            <label htmlFor="field-email" style={labelStyle(fld)}>{r(fld.label, lang)}{fld.required !== false && requiredAsterisk}</label>
            <div style={{ position: 'relative' }}>
              <span style={iconWrapper}>{SvgIcons.email}</span>
              <input id="field-email" type="email" style={inputBase} placeholder={r(fld.placeholder, lang)} value={email} onChange={e => setEmail(e.target.value)} required={fld.required !== false} />
            </div>
          </div>
        )})()}

        {/* Message */}
        {isEnabled('message') && (() => { const fld = getField('message'); return (
          <div>
            {/* FIX: htmlFor + id association for message textarea */}
            <label htmlFor="field-message" style={labelStyle(fld)}>{r(fld.label, lang)}{fld.required !== false && requiredAsterisk}</label>
            <textarea
              id="field-message"
              style={{ ...inputNoIcon, resize: 'vertical', minHeight: `${(fld.rows ?? 3) * 1.75}rem` }}
              placeholder={r(fld.placeholder, lang)}
              value={message}
              onChange={e => setMessage(e.target.value)}
              required={fld.required !== false}
            />
          </div>
        )})()}

        {/* Submit */}
        <button
          type="submit"
          disabled={isLoading}
          style={{
            width: fs.btnFullWidth !== false ? '100%' : 'auto',
            padding: '10px 20px', marginTop: 4, fontFamily: 'inherit',
            cursor: isLoading ? 'not-allowed' : 'pointer',
            fontSize: `${fs.btnFontSize ?? 0.9375}rem`,
            fontWeight: fs.btnFontWeight ?? '600',
            color: fs.btnColor ?? '#ffffff',
            background: isLoading ? `${fs.btnBg ?? '#2563eb'}99` : (fs.btnBg ?? '#2563eb'),
            border: 'none', borderRadius: fs.btnRadius ?? 6, transition: 'opacity 0.2s',
          }}
        >
          {isLoading ? '…' : (r(fs.btnText, lang) || 'Send Message')}
        </button>

      </form>
    </div>
  )
}